import * as THREE from 'three';
import { SITE } from '../config/tuning';
import type { ThreatParams, ThreatPlacement } from '../config/threats';
import type { Checksum, Checksummable } from '../core/Checksum';
import {
  createDetectionTests,
  evaluateDetection,
  LineOfSightCache,
  type DetectionTests,
  type RadarTarget,
} from './Detection';
import type { Terrain } from '../world/Terrain';

/**
 * One radar-guided air defence site, running the state machine of SPEC §3.5.2 exactly:
 *
 * ```
 * DORMANT   ──(target in search range AND line-of-sight)──> SEARCHING
 * SEARCHING ──(continuous detection for t_acquire)────────> TRACKING
 * TRACKING  ──(inside launch envelope AND t_react)───────-> LAUNCHING
 * LAUNCHING ──(missile away)──────────────────────────────> GUIDING
 * GUIDING   ──(lock lost > t_memory)──────────────────────> SEARCHING
 * GUIDING   ──(missile detonated/expired)─────────────────> TRACKING or SEARCHING
 * any       ──(site destroyed)────────────────────────────> DEAD
 * ```
 *
 * The machine is deterministic and *observable*: every transition is legible on the RWR, which is
 * what makes the notch teachable. A site that dropped a track for reasons the player could not
 * read would be indistinguishable from a bug.
 *
 * Two edges are additions to the diagram, both documented at their `SITE` constants and both
 * necessary rather than decorative: TRACKING decays back to SEARCHING on the same `t_memory` the
 * diagram gives GUIDING (otherwise a broken track is permanent), and a site stands down to
 * DORMANT once the target has been out of range for a while (otherwise the RWR fills up with
 * every site the player has ever flown past and stops meaning anything).
 *
 * Units: metres, seconds, m/s.
 */

export const SiteState = {
  Dormant: 'dormant',
  Searching: 'searching',
  Tracking: 'tracking',
  Launching: 'launching',
  Guiding: 'guiding',
  Dead: 'dead',
} as const;
export type SiteStateId = (typeof SiteState)[keyof typeof SiteState];

/** Emitted when the site commits to a shot. M6 turns this into an actual missile. */
export interface LaunchIntent {
  siteId: number;
  designation: string;
  /** Where the missile leaves from, metres. */
  originM: THREE.Vector3;
  /** Salvo index, 0-based. */
  salvoIndex: number;
}

export class AirDefenceSite implements Checksummable {
  readonly id: number;
  readonly designation: string;
  readonly params: ThreatParams;

  /** Ground position of the site, metres. */
  readonly positionM = new THREE.Vector3();
  /** Antenna position: ground plus mast height. The radar horizon is measured from here. */
  readonly radarPositionM = new THREE.Vector3();

  /** The four detection tests as of this site's last evaluation, for the RWR and the debug view. */
  readonly tests: DetectionTests = createDetectionTests();

  state: SiteStateId = SiteState.Dormant;

  /**
   * Whether the mission has this site switched on (SPEC §6.1 `active`, §3.10.1 trigger actions).
   *
   * A disabled site is inert but not dead: it keeps its magazine and its position, so a trigger
   * that activates it later gets a whole site rather than a corpse. Deactivating drops it back to
   * DORMANT rather than leaving a stale track pointing at an aircraft it can no longer see.
   */
  enabled = true;

  /** Seconds of continuous detection accumulated toward `acquireTimeS`. */
  acquireHoldS = 0;
  /** Seconds since the track was last refreshed by a detection. */
  lockLostS = 0;
  /** Seconds the target has been inside the envelope, accumulated toward `reactionTimeS`. */
  reactionHoldS = 0;
  /** Seconds the target has been outside search range while the site is awake. */
  standDownS = 0;
  /** Rounds left before a reload. */
  magazine: number;
  reloadRemainingS = 0;
  salvoRemaining = 0;
  salvoTimerS = 0;
  guidanceS = 0;

  /** Target height above the surface beneath it, metres — refreshed each evaluation. */
  private targetAltitudeAglM = 0;

  private readonly losCache = new LineOfSightCache();

  constructor(id: number, placement: ThreatPlacement, params: ThreatParams, terrain: Terrain) {
    this.id = id;
    this.designation = placement.designation;
    this.params = params;
    this.magazine = params.magazine;

    const groundY = terrain.surfaceHeightAt(placement.xM, placement.zM);
    this.positionM.set(placement.xM, groundY, placement.zM);
    this.radarPositionM.set(placement.xM, groundY + params.radarMastHeightM, placement.zM);
  }

  /** True while the site is radiating and therefore audible to the RWR. */
  get isEmitting(): boolean {
    return (
      this.params.emitsRadar &&
      this.state !== SiteState.Dormant &&
      this.state !== SiteState.Dead
    );
  }

  /** True while the site holds a usable track on the target. */
  get hasTrack(): boolean {
    return (
      this.state === SiteState.Tracking ||
      this.state === SiteState.Launching ||
      this.state === SiteState.Guiding
    );
  }

  /** Acquire time after crew quality, seconds. */
  get effectiveAcquireS(): number {
    return this.params.acquireTimeS * this.crewFactor();
  }

  /** Reaction time after crew quality, seconds. */
  get effectiveReactionS(): number {
    return this.params.reactionTimeS * this.crewFactor();
  }

  reset(): void {
    this.state = SiteState.Dormant;
    this.acquireHoldS = 0;
    this.lockLostS = 0;
    this.reactionHoldS = 0;
    this.standDownS = 0;
    this.magazine = this.params.magazine;
    this.reloadRemainingS = 0;
    this.salvoRemaining = 0;
    this.salvoTimerS = 0;
    this.guidanceS = 0;
    this.losCache.invalidate();
  }

  kill(): void {
    this.state = SiteState.Dead;
  }

  /**
   * Advance the site by one detection period.
   *
   * `dtS` is the detection interval, not the simulation tick: sites are evaluated at 10 Hz and
   * staggered (SPEC §3.5.3), so each one sees a fixed 0.1 s step. Returns a launch intent on the
   * tick the site commits to a shot, otherwise null; the caller turns it into an event.
   *
   * The out-parameter is reused by the caller — nothing here allocates.
   */
  update(
    dtS: number,
    terrain: Terrain,
    target: RadarTarget,
    nowS: number,
    intent: LaunchIntent,
  ): LaunchIntent | null {
    if (this.state === SiteState.Dead) return null;
    if (!this.enabled) {
      // Inert, but intact. Reset the track so activation starts from a cold search rather than
      // resuming a lock the crew could not have been holding while switched off.
      this.state = SiteState.Dormant;
      this.acquireHoldS = 0;
      this.reactionHoldS = 0;
      this.salvoRemaining = 0;
      return null;
    }

    if (this.reloadRemainingS > 0) {
      this.reloadRemainingS -= dtS;
      if (this.reloadRemainingS <= 0) {
        this.reloadRemainingS = 0;
        this.magazine = this.params.magazine;
      }
    }

    // The detection tests always run, whatever the state: the debug view shows them at all times,
    // and a site cannot decide anything without them.
    this.targetAltitudeAglM = target.altitudeAglM;

    // Everything past DORMANT is judged against *track* range, not search range. A site that
    // formed tracks at search range would immediately fail to hold them at track range and
    // oscillate between SEARCHING and TRACKING for as long as the target sat in the gap between
    // the two — which on the RWR would read as a flickering instrument rather than as a radar.
    // DORMANT is the exception: waking up is a search-range decision, per the diagram.
    const baseRangeM =
      this.state === SiteState.Dormant ? this.params.searchRangeM : this.params.trackRangeM;
    const lineOfSight = this.losCache.query(terrain, this.radarPositionM, target.positionM, nowS);
    evaluateDetection(
      terrain,
      this.radarPositionM,
      target,
      baseRangeM,
      this.params.notchVelocityMs,
      this.tests,
      lineOfSight,
    );

    switch (this.state) {
      case SiteState.Dormant:
        this.updateDormant();
        return null;
      case SiteState.Searching:
        this.updateSearching(dtS);
        return null;
      case SiteState.Tracking:
        this.updateTracking(dtS);
        return null;
      case SiteState.Launching:
        return this.updateLaunching(intent);
      case SiteState.Guiding:
        return this.updateGuiding(dtS, intent);
      default:
        return null;
    }
  }

  checksum(into: Checksum): void {
    into.addInt(stateOrdinal(this.state));
    into.addFloat(this.acquireHoldS);
    into.addFloat(this.lockLostS);
    into.addFloat(this.reactionHoldS);
    into.addInt(this.magazine);
    into.addInt(this.salvoRemaining);
    into.addFloat(this.salvoTimerS);
  }

  // --- States ----------------------------------------------------------------------------------

  /**
   * The wake-up is deliberately coarser than a full detection: range and line of sight only, per
   * the diagram. A search radar sweeping its sector does not need to have resolved a target to be
   * radiating, and making the player audible to a site before it can track them is what gives the
   * RWR something to say during the approach.
   */
  private updateDormant(): void {
    if (this.tests.rangeM <= this.params.searchRangeM && this.tests.hasLineOfSight) {
      this.state = SiteState.Searching;
      this.acquireHoldS = 0;
      this.standDownS = 0;
    }
  }

  private updateSearching(dtS: number): void {
    if (this.tests.detected) {
      this.standDownS = 0;
      // "Continuous detection for t_acquire" — a break resets it. That is what makes a brief
      // exposure between two ridges genuinely free, and it is the difference between terrain
      // masking being a tactic and being a decoration.
      this.acquireHoldS += dtS;
      if (this.acquireHoldS >= this.effectiveAcquireS) {
        this.state = SiteState.Tracking;
        this.acquireHoldS = 0;
        this.lockLostS = 0;
        this.reactionHoldS = 0;
      }
      return;
    }

    this.acquireHoldS = 0;
    this.standDown(dtS);
  }

  private updateTracking(dtS: number): void {
    if (this.tests.detected) {
      this.lockLostS = 0;
      this.standDownS = 0;
    } else {
      this.lockLostS += dtS;
      if (this.lockLostS >= this.params.trackMemoryS) {
        this.state = SiteState.Searching;
        this.acquireHoldS = 0;
        this.reactionHoldS = 0;
        return;
      }
    }

    if (!this.inEngagementEnvelope() || !this.canFire()) {
      this.reactionHoldS = 0;
      return;
    }

    this.reactionHoldS += dtS;
    if (this.reactionHoldS < this.effectiveReactionS) return;

    this.state = SiteState.Launching;
    this.salvoRemaining = this.params.salvoSize;
    this.salvoTimerS = 0;
  }

  /** One tick in LAUNCHING: the first round leaves and the site moves to GUIDING. */
  private updateLaunching(intent: LaunchIntent): LaunchIntent | null {
    this.state = SiteState.Guiding;
    this.guidanceS = 0;
    return this.fire(intent);
  }

  private updateGuiding(dtS: number, intent: LaunchIntent): LaunchIntent | null {
    this.guidanceS += dtS;

    if (this.tests.detected) {
      this.lockLostS = 0;
    } else {
      this.lockLostS += dtS;
      if (this.lockLostS >= this.params.trackMemoryS) {
        // Lock lost through the salvo: the rounds already away are M6's problem, the site's is to
        // start looking again.
        this.state = SiteState.Searching;
        this.acquireHoldS = 0;
        this.salvoRemaining = 0;
        return null;
      }
    }

    if (this.salvoRemaining > 0) {
      this.salvoTimerS -= dtS;
      if (this.salvoTimerS <= 0 && this.canFire()) {
        return this.fire(intent);
      }
    }

    // No missiles exist until M6, so nothing will ever report a detonation. The timeout is what
    // resolves the state until that event arrives; it stays afterwards as a backstop.
    if (this.salvoRemaining === 0 && this.guidanceS >= SITE.guidanceTimeoutS) {
      this.state = this.tests.detected ? SiteState.Tracking : SiteState.Searching;
      this.reactionHoldS = 0;
      this.acquireHoldS = 0;
    }
    return null;
  }

  // --- Helpers ---------------------------------------------------------------------------------

  private fire(intent: LaunchIntent): LaunchIntent {
    const salvoIndex = this.params.salvoSize - this.salvoRemaining;
    this.salvoRemaining -= 1;
    this.salvoTimerS = this.params.salvoIntervalS;
    this.magazine -= 1;
    if (this.magazine <= 0) {
      this.magazine = 0;
      this.reloadRemainingS = this.params.reloadS;
      this.salvoRemaining = 0;
    }

    intent.siteId = this.id;
    intent.designation = this.designation;
    intent.originM.copy(this.radarPositionM);
    intent.salvoIndex = salvoIndex;
    return intent;
  }

  private canFire(): boolean {
    return this.magazine > 0 && this.reloadRemainingS <= 0;
  }

  /** Range and altitude both inside the launch envelope (SPEC §3.5.1 catalogue). */
  private inEngagementEnvelope(): boolean {
    const rangeM = this.tests.rangeM;
    if (rangeM < this.params.minEngageM || rangeM > this.params.maxEngageM) return false;
    return (
      this.targetAltitudeAglM >= this.params.minAltM &&
      this.targetAltitudeAglM <= this.params.maxAltM
    );
  }

  private standDown(dtS: number): void {
    if (this.tests.rangeM > this.params.searchRangeM * SITE.wakeHysteresis) {
      this.standDownS += dtS;
      if (this.standDownS >= SITE.standDownS) {
        this.state = SiteState.Dormant;
        this.standDownS = 0;
      }
    } else {
      this.standDownS = 0;
    }
  }

  private crewFactor(): number {
    const quality = Math.max(this.params.crewQuality, SITE.crewQualityFloor);
    return 2 - Math.min(quality, 1);
  }
}

/** Stable ordinal for the checksum; the string values must never reach the hash. */
export function stateOrdinal(state: SiteStateId): number {
  switch (state) {
    case SiteState.Dormant:
      return 0;
    case SiteState.Searching:
      return 1;
    case SiteState.Tracking:
      return 2;
    case SiteState.Launching:
      return 3;
    case SiteState.Guiding:
      return 4;
    case SiteState.Dead:
      return 5;
    default:
      return -1;
  }
}
